/**
 * Rule Evaluator for Form Runtime
 *
 * Evaluates parsed rule AST nodes against the current form state.
 * Designed to be serialized into the Gate iframe runtime.
 */

import { RuleNode } from "./ruleParser";

// ── Evaluation ──

/**
 * Evaluate a parsed rule node against a form values map.
 *
 * @param node - Parsed rule AST node
 * @param values - Current form field values { field_name: value }
 * @returns boolean result of the evaluation
 */
export const evaluateRule = (node: RuleNode, values: Record<string, unknown>): boolean => {
    switch (node.type) {
        case "literal":
            return node.value;

        case "error":
            console.warn(`[prm-form] Rule evaluation skipped (parse error): ${node.message}`, node.raw);
            return true;

        case "logical": {
            const left = evaluateRule(node.left, values);
            if (node.operator === "&&") {
                return left && evaluateRule(node.right, values);
            }
            return left || evaluateRule(node.right, values);
        }

        case "condition": {
            const fieldValue = values[node.field];
            return evaluateCondition(fieldValue, node.operator, node.value);
        }

        default:
            return true;
    }
};

const evaluateCondition = (
    fieldValue: unknown,
    operator: string,
    ruleValue?: string | number | boolean
): boolean => {
    switch (operator) {
        case "empty":
            return isEmpty(fieldValue);

        case "not_empty":
            return !isEmpty(fieldValue);

        case "==":
            return looseEquals(fieldValue, ruleValue);

        case "!=":
            return !looseEquals(fieldValue, ruleValue);

        case ">":
            return toNumber(fieldValue) > toNumber(ruleValue);

        case ">=":
            return toNumber(fieldValue) >= toNumber(ruleValue);

        case "<":
            return toNumber(fieldValue) < toNumber(ruleValue);

        case "<=":
            return toNumber(fieldValue) <= toNumber(ruleValue);

        case "contains":
            return String(fieldValue || "").toLowerCase().includes(String(ruleValue || "").toLowerCase());

        default:
            console.warn(`[prm-form] Unknown rule operator: ${operator}`);
            return true;
    }
};

const isEmpty = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    return false;
};

const looseEquals = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    if (a === null || a === undefined) return b === null || b === undefined || b === "";
    if (b === null || b === undefined) return a === "" || a === null || a === undefined;

    const strA = String(a);
    const strB = String(b);
    if (strA === strB) return true;

    // Try numeric comparison
    const numA = Number(a);
    const numB = Number(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA === numB;

    return false;
};

const toNumber = (value: unknown): number => {
    if (typeof value === "number") return value;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
};

/**
 * Serialize the rule evaluator into a self-contained JavaScript string
 * for embedding in the Gate iframe runtime.
 */
export const serializeRuleEvaluatorForRuntime = (): string => {
    return `
(function() {
    function isEmpty(v) {
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return v.trim() === "";
        if (Array.isArray(v)) return v.length === 0;
        return false;
    }

    function looseEq(a, b) {
        if (a === b) return true;
        if (a == null) return b == null || b === "";
        if (b == null) return a === "" || a == null;
        var sa = String(a), sb = String(b);
        if (sa === sb) return true;
        var na = Number(a), nb = Number(b);
        if (!isNaN(na) && !isNaN(nb)) return na === nb;
        return false;
    }

    function toNum(v) {
        if (typeof v === "number") return v;
        var n = Number(v);
        return isNaN(n) ? 0 : n;
    }

    function evalCond(fv, op, rv) {
        switch (op) {
            case "empty": return isEmpty(fv);
            case "not_empty": return !isEmpty(fv);
            case "==": return looseEq(fv, rv);
            case "!=": return !looseEq(fv, rv);
            case ">": return toNum(fv) > toNum(rv);
            case ">=": return toNum(fv) >= toNum(rv);
            case "<": return toNum(fv) < toNum(rv);
            case "<=": return toNum(fv) <= toNum(rv);
            case "contains": return String(fv || "").toLowerCase().indexOf(String(rv || "").toLowerCase()) !== -1;
            default: return true;
        }
    }

    window.__prmEvalRule = function evalNode(node, values) {
        if (!node) return true;
        switch (node.t) {
            case "b": return node.v;
            case "e":
                console.warn("[prm-form] Rule parse error:", node.m, node.r);
                return true;
            case "l":
                var left = evalNode(node.a, values);
                if (node.o === "&&") return left && evalNode(node.b, values);
                return left || evalNode(node.b, values);
            case "c":
                return evalCond(values[node.f], node.o, node.v);
            default: return true;
        }
    };
})();
`;
};
